import {
  collection,
  limit,
  onSnapshot,
  orderBy,
  query,
  type DocumentData,
  type Timestamp,
  type Unsubscribe,
} from 'firebase/firestore';
import { useEffect, useState } from 'react';
import { db } from '../../lib/firebase';

/**
 * 操作ログ閲覧 hook (admin 第 18 弾、REQ-4.3.26、functions 第 2/3 弾 が書込んだ operationLogs を表示)。
 *
 * 読出 path: `/operationLogs/{logId}` (集合 collection、地区横断、master のみ閲覧可、
 * firestore.rules `allow read: if isMaster()`)。
 *
 * subscribe order: `createdAt desc` (最新が上)、初期 limit は 200 件 (運用で増えたら paginate へ拡張)。
 *
 * 旧 v1 に該当 page 無し → v2 新規 (REQ-4.3.26 MUST を実装、L2 — schema は functions/src/common/auditLog.ts 駆動)。
 */

export type OperationLogsState =
  | { status: 'loading'; items: []; error: null }
  | { status: 'ready'; items: OperationLog[]; error: null }
  | { status: 'error'; items: []; error: Error };

export interface OperationLog {
  id: string;
  schemaVersion: number;
  actorUid: string;
  actorEmail: string | null;
  /** `<entity>.<verb>` 形式 (例: `announcement.created`, `session.unarchived`)。 */
  action: string;
  /** 対象 entity 種別 (`announcement` / `session` / `district` / `master` 等)。 */
  targetType: string;
  targetId: string;
  /** 地区固有操作のとき districtId、地区横断 (master CRUD 等) のとき null。 */
  districtId: string | null;
  /** 自由項目 (entity 固有の subset、PII を含まない原則)。 */
  details: Record<string, unknown>;
  createdAt: Date | null;
}

function tsToDate(value: unknown): Date | null {
  if (!value) return null;
  if (typeof value === 'object' && 'toDate' in value) {
    try {
      return (value as Timestamp).toDate();
    } catch {
      return null;
    }
  }
  return null;
}

export function mapOperationLogDoc(snap: { id: string; data: () => DocumentData }): OperationLog {
  const data = snap.data();
  return {
    id: snap.id,
    // 既存 entry に schemaVersion が無いケースの後方互換 (functions 第 3 弾 で auditLog ヘルパーに
    // schemaVersion: 1 追加した時点より前の entry 想定、`?? 1` で吸収)。
    schemaVersion: typeof data.schemaVersion === 'number' ? data.schemaVersion : 1,
    actorUid: typeof data.actorUid === 'string' ? data.actorUid : 'unknown',
    actorEmail: typeof data.actorEmail === 'string' ? data.actorEmail : null,
    action: typeof data.action === 'string' ? data.action : '',
    targetType: typeof data.targetType === 'string' ? data.targetType : '',
    targetId: typeof data.targetId === 'string' ? data.targetId : '',
    districtId: typeof data.districtId === 'string' ? data.districtId : null,
    details:
      data.details && typeof data.details === 'object' && !Array.isArray(data.details)
        ? (data.details as Record<string, unknown>)
        : {},
    createdAt: tsToDate(data.createdAt),
  };
}

/**
 * 一覧をクライアント側で filter (現状は action prefix と districtId のみ、規模拡大時は server query へ)。
 *
 * - `actionPrefix` 空文字: action filter 無効 (全件返却)
 * - 値あり: `log.action.startsWith(prefix)` で部分一致 (例: `announcement.` → announcement 系全件)
 * - `districtId` null: 地区 filter 無効
 * - 値あり: `log.districtId === districtId` のもののみ (地区横断 entry は除外、null との一致不可)
 */
export function filterOperationLogs(
  logs: OperationLog[],
  opts: { actionPrefix: string; districtId: string | null },
): OperationLog[] {
  return logs.filter((l) => {
    if (opts.actionPrefix !== '' && !l.action.startsWith(opts.actionPrefix)) return false;
    if (opts.districtId !== null && l.districtId !== opts.districtId) return false;
    return true;
  });
}

const DEFAULT_LIMIT = 200;

export function useOperationLogs(maxItems = DEFAULT_LIMIT): OperationLogsState {
  const [state, setState] = useState<OperationLogsState>({
    status: 'loading',
    items: [],
    error: null,
  });

  useEffect(() => {
    const q = query(collection(db, 'operationLogs'), orderBy('createdAt', 'desc'), limit(maxItems));
    const unsub: Unsubscribe = onSnapshot(
      q,
      (snap) => {
        const items = snap.docs.map(mapOperationLogDoc);
        setState({ status: 'ready', items, error: null });
      },
      (error) => setState({ status: 'error', items: [], error }),
    );
    return () => unsub();
  }, [maxItems]);

  return state;
}
